import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from 'react-i18next';
import { confirmAlert } from 'react-confirm-alert';
import styles from './WebChatChannelInfo.module.css';
import * as utils from '../../../../helpers/utils';
import { useRootStore } from '../../../../mobx/bridge';
import ContactInfo from '../ContactInfo/ContactInfo';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const WebChatChannelInfo = observer(function WebChatChannelInfo() {
  const { settingsStore, sendingEvents, commonStore, messageStore } = useRootStore();
  const { t } = useTranslation();
  const timer = React.useRef();
  const [currentTime, setCurrentTime] = React.useState(null);

  const settings = settingsStore.getLoggedUserSettings;

  const webChatContact = settings.SelectedChannel.Contacts.find(
    (c) => c.ContactType.TypeId === utils.ContactTypeEnum.WebChatContact.TypeId
  );

  const welcomeMessage = messageStore.GetWelcomeChannelMessage(settings?.SelectedChannel);
  const closedMessage = messageStore.GetClosedChannelMessage(settings?.SelectedChannel);

  React.useEffect(() => {
    if (welcomeMessage?.Timestamp && closedMessage?.Timestamp) {
      clearInterval(timer.current);
      setCurrentTime(
        utils.getTimeDifferenceInHoursMinutesSeconds(
          new Date(closedMessage.Timestamp),
          new Date(welcomeMessage?.Timestamp)
        )
      );
    } else if (welcomeMessage?.Timestamp && !closedMessage) {
      clearInterval(timer.current);
      const timeToUse = new Date(welcomeMessage.Timestamp);

      timer.current = setInterval(() => {
        setCurrentTime(utils.getTimeDifferenceInHoursMinutesSeconds(new Date(), timeToUse));

        if (closedMessage) {
          clearInterval(timer.current);
        }
      }, 1000);
    }

    return function cleanup() {
      clearInterval(timer.current);
    };
  }, [closedMessage, settings.SelectedChannel, welcomeMessage]);

  React.useEffect(() => {
    if (settings?.SelectedChannel?.CustomerPhoneNumber?.Number) {
      sendingEvents.socketEmitMatchContact(settings.SelectedChannel.CustomerPhoneNumber.Number);
    }
  }, [settings.SelectedChannel, sendingEvents]);

  const closeConversation = () => {
    sendingEvents.socketWebChatEmitCloseConversation(
      settings.SelectedChannel._id,
      commonStore.getLoggedUser._id
    );
  };

  const confirmCloseConversation = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <CustomConfirmAlert
          title={t('Common.Warning')}
          message={t('WebChatChannelInfo.ConfirmCloseConversation')}
          trueText={t('Common.Yes')}
          trueEvent={closeConversation}
          falseText={t('Common.No')}
          falseEvent={onClose}
        />
      ),
    });
  };

  const isChatActive =
    settings?.SelectedChannel?.ChannelStateType?.TypeId !==
    utils.ChannelStateTypeEnum.Closed.TypeId;

  return (
    <React.Fragment>
      <Grid component="div" item xs={12} align="center" className={styles.innerContainer}>
        <div
          className={
            isChatActive ? styles.channelIcon : [styles.channelIcon, styles.closed].join(' ')
          }
        />

        <div className={styles.channelDescription}>{t('WebChatChannelInfo.WebsiteChat')}</div>

        <div className={styles.channelName}>{webChatContact?.getFullName}</div>

        <div className={styles.activity}>
          {t('WebChatChannelInfo.ChatDuration')} {currentTime}
        </div>

        {isChatActive ? (
          <button
            type="button"
            className={styles.closeConversationBtn}
            onClick={confirmCloseConversation}
          >
            {t('WebChatChannelInfo.WebchatCloseButton')}
          </button>
        ) : null}
      </Grid>

      <Grid component="div" item xs={12}>
        <ContactInfo contactToEdit={webChatContact} isWebChatChannel />
      </Grid>
    </React.Fragment>
  );
});

WebChatChannelInfo.whyDidYouRender = true;
WebChatChannelInfo.displayName = 'WebChatChannelInfo';

export default WebChatChannelInfo;
